import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, LayoutTemplate, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { ago } from "@/lib/sk";
import { ICONS } from "@/lib/veiviser";
import { TemplateEditor } from "@/components/maler/TemplateEditor";

const EASE = [0.22, 1, 0.36, 1];

function OwnCard({ t, open, onToggle, depts, onSaved, onDeleted }) {
  const [draftName, setDraftName] = useState(null);
  return (
    <li className="min-w-0 overflow-hidden rounded-[18px] border border-black/[0.07] bg-white shadow-[0_1px_2px_hsl(212_40%_16%/0.04)]" data-testid={`tpl-card-${t.id}`}>
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-black/[0.02]" data-testid={`tpl-card-toggle-${t.id}`}>
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-[hsl(var(--navy)/0.07)] text-[hsl(var(--navy))]"><LayoutTemplate className="h-[18px] w-[18px]" strokeWidth={1.9} /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-semibold" data-testid={`tpl-card-name-${t.id}`}>{(open && draftName?.trim()) || t.name}</span>
          <span className="block truncate text-[12.5px] text-muted-foreground">{[t.note, `${t.jobs.length} ${t.jobs.length === 1 ? "jobb" : "jobber"} · ${t.subs_count} underjobber · ${t.hours} t`, t.uses ? `brukt ${t.uses} ${t.uses === 1 ? "gang" : "ganger"}` : null].filter(Boolean).join(" · ")}</span>
        </span>
        <span className="hidden shrink-0 text-[12px] text-muted-foreground sm:block">{t.updated_by?.name || t.created_by?.name ? `${t.updated_by?.name || t.created_by?.name} · ` : ""}{ago(t.updated_at)}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open ? <TemplateEditor t={t} depts={depts} onSaved={(x) => { setDraftName(null); onSaved(x); }} onDeleted={onDeleted} onClose={() => { setDraftName(null); onToggle(); }} onNameChange={setDraftName} /> : null}
    </li>
  );
}

/** /maler – LOS' own templates: jobs with underjobber and hours, ready for the next quote. The standard templates are listed for reference. */
export default function MalerPage() {
  const [data, setData] = useState(null);
  const [std, setStd] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    api.get("/maler").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/veiviser/katalog").then((r) => setStd(r.data.templates)).catch(() => setStd([]));
  }, []);
  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/maler", { name: "Ny mal", jobs: [{ title: "Overflatebehandling", price_type: "fastpris", subs: [{ title: "Høytrykksspyling", hours: 8, dept: "54" }, { title: "Skraping og flikking", hours: 16, dept: "54" }] }] });
      setData((d) => ({ ...d, templates: [r.data.template, ...(d?.templates || [])] }));
      setOpenId(r.data.template.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCreating(false);
    }
  };
  const saved = (t) => setData((d) => ({ ...d, templates: d.templates.map((x) => (x.id === t.id ? t : x)) }));
  const deleted = (id) => { setData((d) => ({ ...d, templates: d.templates.filter((x) => x.id !== id) })); setOpenId(null); };
  const own = data?.templates || [];
  return (
    <div className="h-full overflow-y-auto bg-[hsl(210_20%_98.5%)]" data-testid="maler-page">
      <motion.div className="mx-auto w-full max-w-[920px] px-5 pb-20 pt-10 sm:px-8" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-[30px] font-bold tracking-[-0.03em] text-[hsl(var(--navy))] sm:text-[36px]" data-testid="maler-title">Maler</h1>
            <p className="mt-2 max-w-[600px] text-[14.5px] leading-[1.6] text-muted-foreground">Egne jobber med underjobber og timer – klare til neste tilbud. Lag en mal her, eller del opp en jobb på et tilbud og velg «Lagre som mal».</p>
          </div>
          <button type="button" onClick={create} disabled={creating} className="inline-flex h-10 items-center gap-2 rounded-full bg-[hsl(var(--navy))] px-5 text-[14px] font-semibold text-white transition-[background-color,transform] hover:bg-[hsl(var(--ink-2))] active:scale-[0.98] disabled:opacity-60" data-testid="maler-new">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Ny mal</button>
        </div>

        <h2 className="mt-10 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Egne maler</h2>
        {!data ? <div className="mt-4 h-20 animate-pulse rounded-[18px] bg-black/[0.04]" />
          : own.length ? <ul className="mt-3 space-y-2.5" data-testid="maler-own">{own.map((t) => <OwnCard key={t.id} t={t} open={openId === t.id} onToggle={() => setOpenId((v) => (v === t.id ? null : t.id))} depts={data.depts} onSaved={saved} onDeleted={deleted} />)}</ul>
            : <p className="mt-3 rounded-[18px] border border-dashed border-black/[0.12] bg-white px-6 py-10 text-center text-[14px] text-muted-foreground" data-testid="maler-empty">Ingen egne maler ennå. Del opp en jobb i underjobber og kryss av for «Lagre som mal» – eller trykk «Ny mal».</p>}

        <h2 className="mt-12 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Standardmaler fra LOS</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">Følger LOS-prislisten og skaleres til fartøyet. Du velger postene når malen brukes, og justerer timer og mengder i kalkylen.</p>
        <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="maler-std">
          {(std || Array.from({ length: 4 })).map((t, i) => {
            if (!t) return <li key={i} className="h-16 min-w-0 animate-pulse rounded-[14px] bg-black/[0.04]" />;
            const Icon = ICONS[t.icon] || ICONS.tom;
            return (
              <li key={t.key} className="flex min-w-0 items-center gap-3 rounded-[14px] bg-white px-4 py-3 shadow-[0_1px_2px_hsl(212_40%_16%/0.04)]" data-testid={`maler-std-${t.key}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[hsl(var(--brand-soft))] text-primary"><Icon className="h-4 w-4" strokeWidth={1.9} /></span>
                <span className="min-w-0 flex-1"><span className="block truncate text-[14px] font-semibold">{t.name}</span><span className="block truncate text-[12px] text-muted-foreground">{t.lead}</span></span>
                <span className="shrink-0 font-mono text-[11.5px] tabular-nums text-muted-foreground">{t.posts} poster</span>
              </li>
            );
          })}
        </ul>
      </motion.div>
    </div>
  );
}
